import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import styled from 'styled-components';
import { colors, fontFamily, s4 } from '@ardoq/design-tokens';
import { caption } from '@ardoq/typography';
import { useEvent } from '@ardoq/hooks';
import { WithPopover } from '@ardoq/popovers';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import { FlexBox } from '@ardoq/layout';
import { BreadcrumbData } from '@ardoq/basic-components';

type OptionBreadcrumbsProps = {
  breadcrumbs: BreadcrumbData[];
};

type Size = {
  width: number;
  height: number;
};

// Moved function from hooks/useElementSize.ts and deleted the file, in order to reduce the number of files in the hooks package
function useElementSize<T extends HTMLElement = HTMLDivElement>(): [
  (node: T | null) => void,
  Size,
] {
  const [ref, setRef] = useState<T | null>(null);
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
  });

  // Prevent too many rendering using useCallback
  const handleSize = useCallback(() => {
    setSize({
      width: ref?.offsetWidth || 0,
      height: ref?.offsetHeight || 0,
    });
  }, [ref?.offsetHeight, ref?.offsetWidth]);

  const resizeEvent = useEvent('resize');
  useEffect(() => {
    handleSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizeEvent]);

  useLayoutEffect(() => {
    handleSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.offsetHeight, ref?.offsetWidth]);

  return [setRef, size];
}

const Container = styled.div`
  display: flex;
  align-items: center;
  ${caption};
  color: ${colors.textSubtle};
  margin-top: ${s4};
  overflow: hidden;
`;

const Breadcrumb = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CalculatedBreadcrumb = styled(Breadcrumb)<{
  $maxBreadcrumbWidth: number;
}>`
  max-width: ${props => props.$maxBreadcrumbWidth}px;
`;

const AllBreadcrumbs = ({ breadcrumbs }: OptionBreadcrumbsProps) => (
  <Container style={{ flexWrap: 'wrap', maxWidth: 448 }}>
    <FlexBox marginRight="xsmall">
      <Icon
        color={colors.icon}
        iconSize={IconSize.SMALL}
        iconName={breadcrumbs[0]?.icon ? breadcrumbs[0]?.icon : IconName.FOLDER}
      />
    </FlexBox>
    {breadcrumbs.map(({ text }, i) => {
      return (
        <FlexBox align="center" key={text + i}>
          {i === breadcrumbs.length - 1 ? (
            <Breadcrumb>{text}</Breadcrumb>
          ) : (
            <>
              <Breadcrumb>{text}</Breadcrumb>

              <Icon
                iconSize={IconSize.SMALL}
                iconName={IconName.CHEVRON_RIGHT}
              />
            </>
          )}
        </FlexBox>
      );
    })}
  </Container>
);

const ICONS_WIDTH = IconSize.SMALL * 4;

// caption typography: fontSize.small (12px) / lineHeight.small (16px)
const BREADCRUMB_LABEL_FONT = `400 12px/16px ${fontFamily.regular}`;
const measureBreadcrumbText = canvasLabelMeasurer(BREADCRUMB_LABEL_FONT);
const getTextWidth = ({ text }: { text: string }) =>
  measureBreadcrumbText(text);

const ICON_SIZE = IconSize.SMALL + 4; // + margin

// example: 📁 breadcrumb1 > breadcrumb2 > ... > breadcrumb4
/**
 * @deprecated Use Breadcrumbs 2.0 instead
 */
export const Breadcrumbs = ({ breadcrumbs }: OptionBreadcrumbsProps) => {
  const [breadcrumbsToRender, setBreadcrumbsToRender] = useState(
    Array(breadcrumbs.length).fill(true)
  );
  const [maxBreadcrumbWidth, setMaxBreadcrumbWidth] = useState(0);
  const [ref, { width: containerWidth }] = useElementSize();

  useEffect(() => {
    if (breadcrumbs.length === 1) {
      setMaxBreadcrumbWidth(containerWidth - ICONS_WIDTH);
      return;
    }

    const updatedMaxBreadcrumbWidth = (containerWidth - ICON_SIZE * 4) / 2;
    setMaxBreadcrumbWidth(updatedMaxBreadcrumbWidth);

    const firstBreadcrumbsWidth =
      Math.min(
        getTextWidth({ text: breadcrumbs[0].text }),
        updatedMaxBreadcrumbWidth
      ) + ICON_SIZE;
    const lastBreadcrumbsWidth = Math.min(
      getTextWidth({ text: breadcrumbs[breadcrumbs.length - 1].text }),
      updatedMaxBreadcrumbWidth
    );

    // Add width for folder icon + first and last breadcrumb
    // example: 📁 breadcrumb1 > breadcrumb4

    const breadcrumbsState = breadcrumbs.reduce(
      (acc, breadcrumb, i) => {
        if (i === 0 || i === breadcrumbs.length - 1) {
          // Always show first and last breadcrumb
          acc.breadcrumbsToRender[i] = true;
          return acc;
        }

        const currentTotalWidth =
          Math.min(
            getTextWidth({ text: breadcrumb.text }),
            updatedMaxBreadcrumbWidth
          ) +
          acc.totalWidth +
          ICON_SIZE;

        if (
          currentTotalWidth < containerWidth &&
          acc.breadcrumbsToRender[i - 1]
        ) {
          acc.totalWidth = currentTotalWidth;
          acc.breadcrumbsToRender[i] = true;
          return acc;
        }

        if (acc.breadcrumbsToRender[i - 1]) {
          // Breadcrumbs is too wide to show, add only size of dots and chevron
          acc.totalWidth += ICON_SIZE * 2;
        }

        acc.breadcrumbsToRender[i] = false;
        return acc;
      },
      {
        totalWidth: ICON_SIZE + firstBreadcrumbsWidth + lastBreadcrumbsWidth,
        breadcrumbsToRender,
      }
    );

    setBreadcrumbsToRender(breadcrumbsState.breadcrumbsToRender);
  }, [
    breadcrumbs,
    containerWidth,
    breadcrumbsToRender,
    setBreadcrumbsToRender,
  ]);

  return (
    <Container ref={ref}>
      <FlexBox marginRight="xsmall">
        <Icon
          color={colors.icon}
          iconSize={IconSize.SMALL}
          iconName={
            breadcrumbs[0]?.icon ? breadcrumbs[0]?.icon : IconName.FOLDER
          }
        />
      </FlexBox>
      {breadcrumbs.map(({ text }, i) => {
        if (i > 0 && !breadcrumbsToRender[i] && !breadcrumbsToRender[i - 1]) {
          return null;
        }
        return (
          <FlexBox align="center" key={text + i}>
            {i === breadcrumbs.length - 1 ? (
              <CalculatedBreadcrumb $maxBreadcrumbWidth={maxBreadcrumbWidth}>
                {text}
              </CalculatedBreadcrumb>
            ) : (
              <>
                {breadcrumbsToRender[i] ? (
                  <CalculatedBreadcrumb
                    $maxBreadcrumbWidth={maxBreadcrumbWidth}
                  >
                    {text}
                  </CalculatedBreadcrumb>
                ) : (
                  <WithPopover
                    content={() => <AllBreadcrumbs breadcrumbs={breadcrumbs} />}
                  >
                    <FlexBox marginRight="xsmall">
                      <Icon
                        style={{ transform: 'rotate(90deg)' }}
                        iconSize={IconSize.SMALL}
                        iconName={IconName.MORE_VERT}
                      />
                    </FlexBox>
                  </WithPopover>
                )}
                <Icon
                  iconSize={IconSize.SMALL}
                  iconName={IconName.CHEVRON_RIGHT}
                />
              </>
            )}
          </FlexBox>
        );
      })}
    </Container>
  );
};
